import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import CarCard from '../components/CarCard';
import SearchBar from '../components/SearchBar';
import CarMap from '../components/CarMap';
import LoadingSpinner from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Filter, X, Map as MapIcon, List, Car } from 'lucide-react';
import { cars as mockCars, carTypes, transmissionTypes } from '../mockData';

const Search = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [filteredCars, setFilteredCars] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTransmission, setSelectedTransmission] = useState([]);
  const [instantBooking, setInstantBooking] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'map'

  useEffect(() => {
    const fetchCars = async () => {
      setLoading(true);
      try {
        const { carsAPI } = await import('../api');
        
        // Build query params
        const params = {
          min_price: priceRange[0],
          max_price: priceRange[1]
        };
        
        const location = searchParams.get('location');
        if (location) params.location = location;
        
        const type = searchParams.get('type');
        if (type) params.car_type = type;
        else if (selectedTypes.length === 1) params.car_type = selectedTypes[0];
        
        if (selectedTransmission.length === 1) params.transmission = selectedTransmission[0];
        if (instantBooking) params.instant_booking = true;
        
        const response = await carsAPI.getAll(params);
        let cars = response.data;
        
        // Client-side filtering for multi-select
        if (selectedTypes.length > 1) {
          cars = cars.filter(car => selectedTypes.includes(car.type));
        }
        if (selectedTransmission.length > 1) {
          cars = cars.filter(car => selectedTransmission.includes(car.transmission));
        }
        
        setFilteredCars(cars);
      } catch (error) {
        console.error('Error fetching cars:', error);
        // Fallback to mock data if API fails
        setFilteredCars(mockCars);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCars();
  }, [searchParams, priceRange, selectedTypes, selectedTransmission, instantBooking]);

  const handleTypeChange = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleTransmissionChange = (transmission) => {
    setSelectedTransmission(prev =>
      prev.includes(transmission) ? prev.filter(t => t !== transmission) : [...prev, transmission]
    );
  };

  const clearFilters = () => {
    setPriceRange([0, 2000]);
    setSelectedTypes([]);
    setSelectedTransmission([]);
    setInstantBooking(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Bar */}
      <div className="bg-white border-b py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SearchBar variant="compact" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-80">
            {/* Mobile Filter Toggle */}
            <Button
              className="lg:hidden w-full mb-4"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              {t('filters')}
            </Button>

            {/* Filters */}
            <Card className={`${showFilters ? 'block' : 'hidden'} lg:block sticky top-20`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{t('filters')}</span>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    {t('clearFilters')}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Price Range */}
                <div>
                  <Label className="text-base font-semibold mb-4 block">{t('priceRange')}</Label>
                  <Slider
                    min={0}
                    max={2000}
                    step={50}
                    value={priceRange}
                    onValueChange={setPriceRange}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>${priceRange[0]} MXN</span>
                    <span>${priceRange[1]} MXN</span>
                  </div>
                </div>

                {/* Car Type */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">{t('carType')}</Label>
                  <div className="space-y-2">
                    {carTypes.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={type.value}
                          checked={selectedTypes.includes(type.value)}
                          onCheckedChange={() => handleTypeChange(type.value)}
                        />
                        <Label htmlFor={type.value} className="cursor-pointer">
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Transmission */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">{t('transmission')}</Label>
                  <div className="space-y-2">
                    {transmissionTypes.map((trans) => (
                      <div key={trans.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={trans.value}
                          checked={selectedTransmission.includes(trans.value)}
                          onCheckedChange={() => handleTransmissionChange(trans.value)}
                        />
                        <Label htmlFor={trans.value} className="cursor-pointer">
                          {trans.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instant Booking */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="instantBooking"
                    checked={instantBooking}
                    onCheckedChange={setInstantBooking}
                  />
                  <Label htmlFor="instantBooking" className="cursor-pointer">
                    {t('instantBooking')}
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : filteredCars.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-gray-600">
                    {filteredCars.length} {filteredCars.length === 1 ? 'auto encontrado' : 'autos encontrados'}
                  </div>
                  <div className="inline-flex border rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => setView('list')}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm ${view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-700'}`}
                      data-testid="view-list-btn"
                    ><List className="w-4 h-4" /> Lista</button>
                    <button
                      onClick={() => setView('map')}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm ${view === 'map' ? 'bg-gray-900 text-white' : 'text-gray-700'}`}
                      data-testid="view-map-btn"
                    ><MapIcon className="w-4 h-4" /> Mapa</button>
                  </div>
                </div>
                {view === 'list' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredCars.map((car) => (
                      <CarCard key={car.id} car={car} />
                    ))}
                  </div>
                ) : (
                  <CarMap
                    cars={filteredCars.map(c => ({
                      id: c.id,
                      name: c.name,
                      price_per_day: c.price_per_day,
                      lat: c.location_coords?.lat,
                      lng: c.location_coords?.lng,
                    }))}
                    onMarkerClick={(c) => window.location.href = `/car/${c.id}`}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <Car className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No se encontraron autos
                </h3>
                <p className="text-gray-600 mb-4">
                  Intenta ajustar tus filtros o buscar en otra ubicación
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;